import React from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import Analytics from '../components/Analytics.jsx'
import Products from '../components/Products.jsx'
import InstallmentsPage from '../components/payments/InstallmentsPage.jsx'
import Purchases from '../components/Purchases.jsx'
import Sales from '../components/Sales.jsx'
import Expenses from '../components/Expenses.jsx'
import Settings from '../components/Settings.jsx'
import Auth from '../components/Auth.jsx'
import Signup from '../components/Signup.jsx'
import ForgotPassword from '../components/ForgotPassword.jsx'
import ResetPassword from '../components/ResetPassword.jsx'
import VerifyEmail from '../components/VerifyEmail.jsx'
import OnboardingWizard from '../components/OnboardingWizard.jsx'
import Customers from '../components/Customers.jsx'
import CustomerDetail from '../components/customers/CustomerDetail.jsx'
import MonCompte from '../components/MonCompte.jsx'
import Prestations from '../components/Prestations.jsx'
import Agenda from '../components/Agenda.jsx'
import Relances from '../components/Relances.jsx'
import Quotes from '../components/Quotes.jsx'
import AuditTrail from '../components/admin/AuditTrail.jsx'
import Employees from '../components/Employees.jsx'
import { OrdersPage } from '../features/orders/OrdersPage'
import * as FinanceMod from '../components/finance/Finance.jsx'
import * as FinanceJournalMod from '../components/finance/FinanceJournal.jsx'
import * as FinanceExportMod from '../components/finance/FinanceExport.jsx'
import * as FinanceAccountsMod from '../components/finance/FinanceAccounts.jsx'
import * as TrialBalanceMod from '../components/finance/TrialBalance.jsx'
import FinanceLedger from '../components/finance/FinanceLedger.jsx'
import FinanceCashflow from '../components/finance/FinanceCashflow.jsx'
import FinanceProfitLoss from '../components/finance/FinanceProfitLoss.jsx'
import FinanceVat from '../components/finance/FinanceVat.jsx'
import FinancePeriods from '../components/finance/FinancePeriods.jsx'
import FinanceAudit from '../components/finance/FinanceAudit.jsx'
import { RequireAuth, RequireModule, RequireRole } from './guards.jsx'
import { landingPathForRoles } from './routeUtils.js'

function pickComponent(mod, fallbackName) {
  return mod?.default || (fallbackName ? mod?.[fallbackName] : null) || null
}

const Finance = pickComponent(FinanceMod, 'Finance')
const FinanceJournal = pickComponent(FinanceJournalMod, 'FinanceJournal')
const FinanceExport = pickComponent(FinanceExportMod, 'FinanceExport')
const FinanceAccounts = pickComponent(FinanceAccountsMod, 'FinanceAccounts')
const TrialBalance = pickComponent(TrialBalanceMod, 'TrialBalance')

function MissingFinance({ name }) {
  return (
    <div className="p-6">
      <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-4">
        <div className="font-semibold">Module manquant: {name}</div>
        <div className="text-sm mt-1">
          Verifie l'export du fichier (idealement <code>export default</code>).
        </div>
      </div>
    </div>
  )
}

export default function AppRoutes({
  connected,
  user,
  onLoggedIn,
  products,
  reloadProducts,
  onCreateSale,
  hasRole,
  isModuleEnabled,
  layoutElement,
}) {
  const authRouteElement = connected ? (
    <Navigate to={landingPathForRoles(user?.roles || [])} replace />
  ) : (
    <Auth onLoggedIn={onLoggedIn} />
  )

  return (
    <Routes>
      <Route path="/auth" element={authRouteElement} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/installments" element={<InstallmentsPage />} />

      <Route element={layoutElement}>
        <Route element={<RequireAuth user={user} />}>
          <Route path="/" element={<Navigate to="/orders" replace />} />
          <Route path="/onboarding" element={<OnboardingWizard />} />

          <Route
            element={
              <RequireModule
                enabled={isModuleEnabled('orders')}
                moduleKey="orders"
                canManage={hasRole('admin') || hasRole('superadmin')}
              />
            }
          >
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders/:id" element={<OrdersPage />} />
          </Route>

          <Route
            element={
              <RequireModule
                enabled={isModuleEnabled('customers')}
                moduleKey="customers"
                canManage={hasRole('admin') || hasRole('superadmin')}
              />
            }
          >
            <Route path="/customers" element={<Customers />} />
            <Route path="/customers/:uid" element={<CustomerDetail />} />
          </Route>

          <Route
            element={
              <RequireModule
                enabled={isModuleEnabled('quotes')}
                moduleKey="quotes"
                canManage={hasRole('admin') || hasRole('superadmin')}
              />
            }
          >
            <Route path="/quotes" element={<Quotes />} />
          </Route>

          <Route element={<RequireRole user={user} allowed={['sales', 'admin', 'superadmin']} />}>
            <Route
              element={
                <RequireModule
                  enabled={isModuleEnabled('sales')}
                  moduleKey="sales"
                  canManage={hasRole('admin') || hasRole('superadmin')}
                />
              }
            >
              <Route
                path="/sales"
                element={
                  <Sales
                    products={products}
                    reloadProducts={reloadProducts}
                    onCreate={onCreateSale}
                  />
                }
              />
            </Route>
          </Route>

          <Route element={<RequireRole user={user} allowed={['ops', 'admin', 'superadmin']} />}>
            <Route
              element={
                <RequireModule
                  enabled={isModuleEnabled('products')}
                  moduleKey="products"
                  canManage={hasRole('admin') || hasRole('superadmin')}
                />
              }
            >
              <Route
                path="/products"
                element={<Products products={products} reloadProducts={reloadProducts} />}
              />
            </Route>
            <Route
              element={
                <RequireModule
                  enabled={isModuleEnabled('purchases')}
                  moduleKey="purchases"
                  canManage={hasRole('admin') || hasRole('superadmin')}
                />
              }
            >
              <Route
                path="/purchases/*"
                element={<Purchases products={products} reloadProducts={reloadProducts} />}
              />
            </Route>
            <Route
              element={
                <RequireModule
                  enabled={isModuleEnabled('expenses')}
                  moduleKey="expenses"
                  canManage={hasRole('admin') || hasRole('superadmin')}
                />
              }
            >
              <Route path="/expenses" element={<Expenses />} />
            </Route>
            <Route path="/projects" element={<Navigate to="/prestations" replace />} />
            <Route
              element={
                <RequireModule
                  enabled={isModuleEnabled('prestations')}
                  moduleKey="prestations"
                  canManage={hasRole('admin') || hasRole('superadmin')}
                />
              }
            >
              <Route path="/prestations" element={<Prestations />} />
            </Route>
            <Route
              element={
                <RequireModule
                  enabled={isModuleEnabled('agenda')}
                  moduleKey="agenda"
                  canManage={hasRole('admin') || hasRole('superadmin')}
                />
              }
            >
              <Route path="/agenda" element={<Agenda />} />
            </Route>
            <Route
              element={
                <RequireModule
                  enabled={isModuleEnabled('relances')}
                  moduleKey="relances"
                  canManage={hasRole('admin') || hasRole('superadmin')}
                />
              }
            >
              <Route path="/relances" element={<Navigate to="/communications" replace />} />
              <Route path="/communications" element={<Relances />} />
            </Route>
            <Route path="/campaigns" element={<Navigate to="/communications" replace />} />
          </Route>

          <Route element={<RequireRole user={user} allowed={['admin', 'superadmin', 'bi']} />}>
            <Route
              element={
                <RequireModule
                  enabled={isModuleEnabled('analytics')}
                  moduleKey="analytics"
                  canManage={hasRole('admin') || hasRole('superadmin')}
                />
              }
            >
              <Route path="/analytics" element={<Analytics />} />
            </Route>
            <Route path="/settings" element={<Settings />} />
          </Route>

          <Route element={<RequireRole user={user} allowed={['ops', 'admin', 'superadmin']} />}>
            <Route
              element={
                <RequireModule
                  enabled={isModuleEnabled('employees')}
                  moduleKey="employees"
                  canManage={hasRole('admin') || hasRole('superadmin')}
                />
              }
            >
              <Route path="/employees" element={<Employees />} />
            </Route>
          </Route>

          <Route element={<RequireRole user={user} allowed={['auditor', 'superadmin']} />}>
            <Route
              element={
                <RequireModule
                  enabled={isModuleEnabled('audit')}
                  moduleKey="audit"
                  canManage={hasRole('admin') || hasRole('superadmin')}
                />
              }
            >
              <Route path="/admin/audit" element={<AuditTrail />} />
            </Route>
          </Route>

          <Route path="/mon-compte" element={<MonCompte />} />

          <Route element={<RequireRole user={user} allowed={['superadmin']} />}>
            <Route
              element={
                <RequireModule
                  enabled={isModuleEnabled('finance')}
                  moduleKey="finance"
                  canManage={hasRole('superadmin')}
                />
              }
            >
              <Route
                path="/finance"
                element={Finance ? <Finance /> : <MissingFinance name="Finance.jsx" />}
              />
              <Route
                path="/finance/journal"
                element={
                  FinanceJournal ? <FinanceJournal /> : <MissingFinance name="FinanceJournal.jsx" />
                }
              />
              <Route
                path="/finance/accounts"
                element={
                  FinanceAccounts ? (
                    <FinanceAccounts />
                  ) : (
                    <MissingFinance name="FinanceAccounts.jsx" />
                  )
                }
              />
              <Route path="/finance/ledger" element={<FinanceLedger />} />
              <Route path="/finance/profit-loss" element={<FinanceProfitLoss />} />
              <Route path="/finance/vat" element={<FinanceVat />} />
              <Route path="/finance/cashflow" element={<FinanceCashflow />} />
              <Route path="/finance/periods" element={<FinancePeriods />} />
              <Route path="/finance/audit" element={<FinanceAudit />} />
              <Route
                path="/finance/trial-balance"
                element={
                  TrialBalance ? <TrialBalance /> : <MissingFinance name="TrialBalance.jsx" />
                }
              />
              <Route
                path="/finance/export"
                element={
                  FinanceExport ? <FinanceExport /> : <MissingFinance name="FinanceExport.jsx" />
                }
              />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/orders" replace />} />
      </Route>
    </Routes>
  )
}
